import {
  BellRing,
  CalendarClock,
  Check,
  CornerDownRight,
  GitFork,
  Link2,
  CircleSlash,
  Clock,
  Pause,
  ShieldQuestion,
  Star,
  X,
} from 'lucide-react';
import type { TicketState } from '../lib/api.js';
import { ago, dateTime, until, STATE_LABEL, STATE_SHORT } from '../lib/format.js';
import { slaVerdict } from '../lib/sla.js';
import { Badge, type Tone } from './Badge.js';
import { Tooltip } from './Overlays.js';

/**
 * Domain badges.
 *
 * These live in the UI kit rather than inside a page because the same state
 * has to look identical in the queue, on the ticket, and in a report — and
 * because the mapping from a state to a tone is a product decision, not a
 * per-page styling choice.
 */

const STATE_TONE: Record<TicketState, Tone> = {
  new: 'info',
  open: 'brand',
  pending_requester: 'neutral',
  pending_internal: 'neutral',
  pending_event: 'neutral',
  // Blocked on a third party is the one waiting state that should catch the
  // eye, because it is the one nobody on the desk can clear.
  pending_external: 'accent',
  resolved: 'ok',
  closed: 'neutral',
  cancelled: 'neutral',
};

export function StateBadge({ state, full = false }: { state: TicketState; full?: boolean }) {
  return (
    <Badge
      tone={STATE_TONE[state]}
      dot
      title={STATE_LABEL[state]}
      className={state === 'cancelled' ? 'line-through' : undefined}
    >
      {full ? STATE_LABEL[state] : STATE_SHORT[state]}
    </Badge>
  );
}

/**
 * SLA position. Colour never carries this alone — the word does, so it holds
 * up in greyscale, in a screenshot, and for a colour-blind reader.
 */
export function SlaBadge({
  breached,
  dueAt,
  paused,
  state,
}: {
  breached: boolean;
  dueAt: string | null;
  paused: boolean;
  /** Omitted only where the caller genuinely has no state to hand. */
  state?: TicketState;
}) {
  // THE VERDICT COMES FIRST, before breached and before paused. A finished
  // ticket that missed its target reads "Missed", not "Overdue": overdue is
  // something you act on, and there is nothing left to do here.
  const verdict = state
    ? slaVerdict({ state, resolution_breached: breached, resolution_due_at: dueAt })
    : 'live';
  if (verdict === 'missed') {
    return (
      <Tooltip content="Resolved after the target. The clock has stopped.">
        <Badge tone="danger" icon={<X />}>
          Missed
        </Badge>
      </Tooltip>
    );
  }
  if (verdict === 'met') {
    return (
      <Tooltip content="Resolved within the target.">
        <Badge tone="ok" icon={<Check />}>
          Met
        </Badge>
      </Tooltip>
    );
  }
  // Cancelled and no-target both decline to give a verdict, for different
  // reasons: one had no target, the other had no work.
  if (verdict !== 'live') return <span className="text-2xs text-ink-tertiary">—</span>;

  if (breached) {
    return (
      <Badge tone="danger" dot>
        Overdue
      </Badge>
    );
  }
  if (paused) {
    return (
      <Tooltip content="The resolution clock is paused while this ticket waits on somebody else.">
        <Badge tone="neutral" icon={<Pause />}>
          Paused
        </Badge>
      </Tooltip>
    );
  }
  if (!dueAt) return <span className="text-2xs text-ink-tertiary">—</span>;

  const hoursLeft = (new Date(dueAt).getTime() - Date.now()) / 3_600_000;
  if (hoursLeft < 0) {
    return (
      <Badge tone="danger" dot>
        Overdue
      </Badge>
    );
  }
  if (hoursLeft < 4) {
    return (
      <Tooltip content={`Due in about ${Math.max(1, Math.round(hoursLeft))} business hours.`}>
        <Badge tone="warn" icon={<Clock />}>
          At risk
        </Badge>
      </Tooltip>
    );
  }
  return (
    <Badge tone="ok" dot>
      On track
    </Badge>
  );
}

/**
 * Router confidence — the measured purity from six months of outcomes, and a
 * plain statement when the router refused to guess.
 */
export function ConfidenceBadge({
  confidence,
  needsHuman,
}: {
  confidence: string | number | null;
  needsHuman: boolean;
}) {
  if (needsHuman) {
    return (
      <Tooltip content="Below the auto-route threshold, so a person decides. For travel and facilities the tie-breaker is the requester's office, not the wording.">
        <Badge tone="warn" icon={<ShieldQuestion />}>
          Needs routing
        </Badge>
      </Tooltip>
    );
  }
  if (confidence === null) return <span className="text-2xs text-ink-tertiary">—</span>;

  const c = typeof confidence === 'string' ? Number(confidence) : confidence;
  if (!Number.isFinite(c)) return <span className="text-2xs text-ink-tertiary">—</span>;

  return (
    <Tooltip content="Measured routing purity: the share of this request type that landed in this queue over six months.">
      <span className="nums text-xs text-ink-secondary">{Math.round(c * 100)}%</span>
    </Tooltip>
  );
}

export function SourceBadge({ source }: { source: string }) {
  const label =
    { email: 'Email', portal: 'Portal', phone: 'Phone', teams: 'Teams', api: 'API', automation: 'Auto', system: 'System' }[
      source
    ] ?? source;
  return <span className="text-3xs uppercase tracking-wide text-ink-tertiary">{label}</span>;
}

export function CancelledMark() {
  return <CircleSlash aria-hidden className="h-3 w-3 text-ink-tertiary" />;
}

/**
 * Marks a requester the firm considers senior (FR-VIP-02).
 *
 * ONE COMPONENT, used by the queue row, the peek pane and the detail header.
 * A decoration that appears on some of those and not others is worse than
 * none at all: an agent who sees it in the peek and not in the list stops
 * trusting the list.
 *
 * It says WHY on hover. The verdict comes from a substring rule against a
 * job title, so it is going to be wrong about somebody, and a badge that
 * cannot explain itself gets ignored the first time it is.
 */
export function VipBadge({
  title,
  compact = false,
}: {
  /** Their job title, which is the reason the badge is there. */
  title?: string | null;
  /** In a dense list, the star alone. */
  compact?: boolean;
}) {
  const why = title
    ? `${title} — flagged as senior by the firm's VIP rule`
    : "Flagged as senior by the firm's VIP rule";

  if (compact) {
    return (
      <Tooltip content={why}>
        <span className="inline-flex shrink-0 text-accent" aria-label={why}>
          <Star aria-hidden className="h-3.5 w-3.5 fill-current" />
        </span>
      </Tooltip>
    );
  }

  return (
    <Tooltip content={why}>
      <Badge tone="accent" icon={<Star className="fill-current" />}>
        VIP
      </Badge>
    </Tooltip>
  );
}

/**
 * A ticket that came back on its own, and one that is still away.
 *
 * THE RETURN IS THE HALF THAT MATTERS. A parked ticket leaves the queue on a
 * promise; when the date arrives it reappears among forty others and looks
 * identical to every ticket that was open all along. Without a mark the
 * feature quietly becomes "tickets vanish for three weeks and then do not
 * come to your attention", which is worse than never parking them.
 *
 * So the returned badge is loud — a warn tone and a named event — and the
 * waiting badge is quiet, because a ticket that is correctly away needs to be
 * legible, not urgent. The two are one component because they are one fact
 * seen from either side of its date, and splitting them invites a row that
 * renders both.
 *
 * It clears itself: the server drops `event_fired_at` the moment anybody acts
 * on the ticket. A flag that stays lit after it has been answered is
 * decoration, and decoration is what people learn to stop seeing.
 */
export function EventBadge({
  firedAt,
  dueAt,
  eventName,
}: {
  firedAt: string | null;
  dueAt: string | null;
  eventName: string | null;
}) {
  const name = eventName ?? 'Event';

  if (firedAt) {
    return (
      <Tooltip content={`${name} — came back ${ago(firedAt)}. This is here because its date arrived.`}>
        <Badge tone="warn" icon={<BellRing />}>
          {eventName ? `${eventName} — back` : 'Came back'}
        </Badge>
      </Tooltip>
    );
  }

  if (!dueAt) return null;

  return (
    <Tooltip content={`Parked until ${dateTime(dueAt)}. It comes back by itself, to whoever parked it.`}>
      <Badge tone="neutral" icon={<CalendarClock />}>
        Back {until(dueAt)}
      </Badge>
    </Tooltip>
  );
}

/**
 * That a ticket is part of something bigger, or is the something bigger.
 *
 * THE CUE BELONGS IN THE QUEUE, not only on the ticket. An onboarding request
 * and its two children sit in the same list looking like three unrelated
 * jobs; the desk then works the laptop, resolves it, and nobody notices the
 * account half is still open. A mark on the row is what makes the family
 * visible at the point where the work is actually picked.
 *
 * BOTH ENDS READ THE SAME WAY: "<relationship> of <ticket>". The first
 * version said "This request became 1 ticket" — vague and ungrammatical at
 * one — and the second said "Parent of 3", which leaves the reader asking
 * three what. A bare count next to a badge that names a ticket is two
 * different sentences on the same row.
 *
 * So the parent names a child and counts the rest: "Parent of #120206 and 2
 * more". It stays a complete phrase at every size, never dangles a number,
 * and stays narrow enough not to squeeze the subject beside it. The tooltip
 * names them all, and the words match the Related panel's own headings.
 *
 * Both badges can show at once: a ticket in the middle of a chain is a child
 * of one thing and the parent of another, and hiding either half would make
 * the row lie by omission.
 */
export function FamilyBadge({
  parentNumber,
  childNumbers,
  relatedNumbers = [],
}: {
  parentNumber: number | null;
  /** The tickets this one is the parent of. */
  childNumbers: number[];
  /** The tickets linked to this one as related. */
  relatedNumbers?: number[];
}) {
  return (
    <span className="inline-flex items-center gap-1">
      {parentNumber !== null && (
        <Tooltip content={`This ticket is a child of #${parentNumber}.`}>
          <Badge tone="neutral" icon={<CornerDownRight />}>
            Child of #{parentNumber}
          </Badge>
        </Tooltip>
      )}
      {childNumbers.length > 0 && (
        <Tooltip content={`This ticket is the parent of ${listNumbers(childNumbers, 6)}.`}>
          <Badge tone="info" icon={<GitFork />}>
            Parent of {listNumbers(childNumbers, 1)}
          </Badge>
        </Tooltip>
      )}
      {relatedNumbers.length > 0 && (
        <Tooltip content={`This ticket is related to ${listNumbers(relatedNumbers, 6)}.`}>
          <Badge tone="neutral" icon={<Link2 />}>
            Related to {listNumbers(relatedNumbers, 1)}
          </Badge>
        </Tooltip>
      )}
    </span>
  );
}

/**
 * "#1", "#1 and #2", "#1, #2 and #3", "#1 and 4 more".
 *
 * Exported for its own test. The sentence this feeds was wrong at exactly one
 * item, which is the case a reader is most likely to meet and the one a
 * hand-check skips.
 *
 * `cap` is how many to name before counting the rest: the badge names one
 * because it sits beside a subject it must not squeeze, the tooltip names
 * several because it has the room.
 */
export function listNumbers(numbers: number[], cap: number): string {
  const shown = numbers.slice(0, Math.max(1, cap)).map((n) => `#${n}`);
  const rest = numbers.length - shown.length;

  const joined =
    shown.length === 1
      ? shown[0]!
      : `${shown.slice(0, -1).join(', ')} and ${shown[shown.length - 1]!}`;

  if (rest === 0) return joined;
  // "#1, and 2 more" reads as a mistake after a single item; "#1 and 2 more"
  // does not. The comma only earns its place once a list has commas already.
  return shown.length === 1 ? `${joined} and ${rest} more` : `${joined}, and ${rest} more`;
}
